/**
 * Utility for lazy loading Redux reducers
 * Use this to load reducers on-demand when routes are accessed
 */

import { lazyReducers } from "../slices";

// Cache to prevent loading the same reducer multiple times
const loadedReducers = new Set();

/**
 * Load a reducer dynamically
 * @param {string} reducerKey - The key of the reducer to load (e.g., 'Fruits', 'Parties')
 * @returns {Promise} - Promise that resolves when reducer is loaded
 */
export const loadReducer = async (reducerKey) => {
  // Check if reducer is already loaded
  if (loadedReducers.has(reducerKey)) {
    return Promise.resolve();
  }

  // Check if reducer exists in lazy reducers
  if (!lazyReducers[reducerKey]) {
    console.warn(`Reducer "${reducerKey}" not found in lazy reducers`);
    return Promise.resolve();
  }

  try {
    // Get reducer manager from window (set in index.js)
    const reducerManager = window.__REDUCER_MANAGER__;

    if (!reducerManager) {
      console.error("Reducer manager not found. Make sure the app is initialized correctly.");
      return Promise.resolve();
    }

    // Check if reducer is already registered
    if (reducerManager.has(reducerKey)) {
      loadedReducers.add(reducerKey);
      return Promise.resolve();
    }

    // Load the reducer module
    const reducer = await lazyReducers[reducerKey]();
    reducerManager.add(reducerKey, reducer);
    loadedReducers.add(reducerKey);

    return Promise.resolve();
  } catch (error) {
    console.error(`Error loading reducer "${reducerKey}":`, error);
    return Promise.reject(error);
  }
};

/**
 * Load multiple reducers at once
 * @param {string[]} reducerKeys - Array of reducer keys to load
 * @returns {Promise} - Promise that resolves when all reducers are loaded
 */
export const loadReducers = async (reducerKeys) => {
  const promises = reducerKeys.map(key => loadReducer(key));
  return Promise.all(promises);
};

/**
 * Map route paths to their required reducers
 * This helps automatically load reducers when routes are accessed
 */
export const routeReducerMap = {
  "/settings-invoice": ["invoiceTypes", "invoiceSubtypes", "invoiceParticulars"],
  "/settings-invoice-type": ["invoiceTypes"],
  "/settings-invoice-subtype": ["invoiceSubtypes", "invoiceTypes"],
  "/settings-invoice-particulars": ["invoiceParticulars", "invoiceSubtypes"],
  "/settings-shipping-port": ["shippingPorts"],
  "/settings-fruits-items": ["Fruits", "FruitsSubtypes"],
  "/settings-fruits-types": ["FruitsSubtypes", "Fruits"],
  "/party-management": ["Parties"],
  "/import": ["Containers", "containerInvoices", "invoiceTypes", "invoiceSubtypes", "invoiceParticulars", "shippingPorts", "Parties", "Fruits", "FruitsSubtypes", "stock"],
  "/export": ["stock", "invoiceTypes", "Parties"],
  "/miscellaneous": ["invoiceTypes", "Parties"],
};

/**
 * Load reducers for a specific route path
 * @param {string} pathname - The route pathname
 * @returns {Promise} - Promise that resolves when reducers are loaded
 */
export const loadReducersForRoute = async (pathname) => {
  const reducerKeys = routeReducerMap[pathname];

  if (!reducerKeys || reducerKeys.length === 0) {
    return Promise.resolve();
  }

  return loadReducers(reducerKeys);
};
